import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Building2,
  Check,
  Copy,
  Infinity as InfinityIcon,
  Layers,
  Pencil,
  Plus,
  Search,
  Sparkles,
  TrendingUp,
  Trash2,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { planCardFeatures } from '../../lib/feature-registry'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { FilterRow } from '../../components/filters/FilterRow'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { TextField, FieldLabel } from '../../components/ui/Field'
import { api, extractApiError } from '../../lib/api'
import { plansResponse } from '../../lib/types'
import { formatMoney, formatNumber } from '../../lib/format'

const TYPE_OPTIONS = [
  { value: 'trial', label: 'Trial', hint: 'Free for a limited time' },
  { value: 'paid', label: 'Paid', hint: 'Subscription pricing' },
]

const FILTER_CHIPS = [
  { value: 'all', label: 'All plans' },
  { value: 'trial', label: 'Trial' },
  { value: 'paid', label: 'Paid' },
  // "Inactive" = a switched-off plan (Plan.isActive === false). Label matches
  // the editor's Active toggle. "Default" (Plan.isDefault) and "Most Popular"
  // (isPopular) are separate marketing concepts and intentionally NOT filters
  // here.
  { value: 'inactive', label: 'Inactive' },
]

/** Left-aligned price headline matching the pricing-page reference:
 *  a large bold value with a small inline suffix. Four states keyed off
 *  the plan `type` + price (trial / custom / free / paid). */
// Currencies a plan's price can be set + displayed in. Codes match the
// 3-letter Plan.currency field; symbols drive every price headline.
/** Name-aware trial classification — mirrors the backend single source of
 *  truth (`isTrialPlan` in billing/trial-policy.js) that backs the
 *  Companies-page "Trial" pill/KPI: a plan is a trial when its stored `type`
 *  is 'trial' OR its name/code contains "trial" (case-insensitive). This is
 *  why a plan named "Free Trial" but saved as type:'paid' still counts as a
 *  trial — so the Plans page Trial filter + KPI stop disagreeing with the
 *  Companies page. Prefers the server-sent `isTrial`; the regex is a fallback
 *  for payloads from before that field shipped. */
const planIsTrial = (p) =>
  p?.isTrial ??
  (p?.type === 'trial' ||
    /trial/i.test(`${p?.code ?? ''} ${p?.name ?? ''}`))

const CURRENCY_CODES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD']

function PriceBlock({
  type,
  monthlyPrice,
  yearlyPrice,
  cadence = 'monthly',
  currency,
  trialDays,
  // Name-aware trial flag (from planIsTrial). When set, the headline renders
  // the "Free — trial" treatment even if the stored `type` is 'paid' (e.g. a
  // plan named "Free Trial"), matching the Trial filter + Companies pill.
  isTrial = false,
}) {
  const big =
    'font-display text-3xl font-bold text-[color:var(--color-ink-strong)] leading-none tracking-tight'
  const suffixClass = 'text-sm text-[color:var(--color-ink-muted)]'

  if (isTrial || type === 'trial') {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={big}>Free</span>
        <span className={suffixClass}>
          {trialDays ? `${trialDays}-day trial` : 'trial'}
        </span>
      </div>
    )
  }

  // Show the SELECTED cadence's price, falling back to the other cadence
  // when the selected one isn't offered (a yearly-only plan under the
  // Monthly toggle still shows its yearly price, and vice versa).
  const toNum = (v) =>
    v === null || v === undefined || v === '' ? null : Number(v)
  const m = toNum(monthlyPrice)
  const y = toNum(yearlyPrice)
  const hasM = m !== null && !Number.isNaN(m)
  const hasY = y !== null && !Number.isNaN(y) && y > 0
  const showYearly = cadence === 'yearly' ? hasY : !hasM && hasY
  const num = showYearly ? y : hasM ? m : null
  const suffix = showYearly ? 'per year' : 'per month'

  if (num === null || Number.isNaN(num)) {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={big}>Custom</span>
        <span className={suffixClass}>tailored to your org</span>
      </div>
    )
  }
  return (
    <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
      {/* min-w-0 + break-all so an over-long value wraps inside the card
       *  instead of pushing the layout wide (the caps in validate() stop
       *  it reaching that size on save; this contains it while typing). */}
      <span className={`${big} tabular-nums min-w-0 break-all`}>
        {formatMoney(num, currency, { decimals: Number.isInteger(num) ? 0 : 2 })}
      </span>
      <span className={suffixClass}>{num === 0 ? 'forever free' : suffix}</span>
    </div>
  )
}

/** Compact "unlimited-aware" cap value — ∞ glyph for null, formatted
 *  number otherwise. */
function fmtCap(n) {
  if (n === null || n === undefined)
    return <InfinityIcon size={14} aria-label="Unlimited" />
  return (
    <span className="tabular-nums break-all">
      {formatNumber(Number(n))}
    </span>
  )
}

/** Boxed resource caps — tinted sub-card, right-aligned values. Mirrors
 *  the Sign-up + Billing cards so the three pricing surfaces match. */
function CapBox({ plan }) {
  return (
    <div className="mt-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4 space-y-2.5">
      <CapRow label="Max Users" value={plan.maxUsers} />
      <CapRow label="Storage" value={plan.storageGb} suffix="GB" />
      <CapRow label="AI Tokens" value={plan.aiTokensPerMonth} />
    </div>
  )
}

function CapRow({ label, value, suffix }) {
  return (
    <div className="flex items-center justify-between gap-2 min-w-0">
      <span className="shrink-0 text-sm text-[color:var(--color-ink-muted)]">
        {label}
      </span>
      <span className="inline-flex items-baseline gap-1 min-w-0 text-right text-sm font-semibold text-[color:var(--color-ink-strong)]">
        {fmtCap(value)}
        {suffix && value !== null && value !== undefined ? (
          <span className="shrink-0 text-xs font-normal text-[color:var(--color-ink-muted)]">
            {suffix}
          </span>
        ) : null}
      </span>
    </div>
  )
}

// Note: the old `TypePill` (TRIAL / PAID badges) was removed from
// the plan cards per request — the trial/paid distinction now
// reads from the price treatment itself ("14-day trial" vs
// "$8 /month"). The function was deleted along with its only
// callsite; if a future card needs the badge back, lift it from
// git history (was a small top-of-card pill component).

export function PlansPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  // Page-level billing-cycle toggle — switches every card's headline price
  // between monthly and yearly. Only surfaced when a yearly price exists.
  const [cadence, setCadence] = useState('monthly')

  // A filter is "active" when search is non-empty or the chip filter
  // isn't "all". Drives the Clear-all-filters button in the toolbar.
  const hasAnyFilter = search.trim() !== '' || filter !== 'all'

  function clearFilters() {
    setSearch('')
    setFilter('all')
  }

  const plansQuery = useQuery({
    queryKey: ['platform-plans'],
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/super-admin/plans/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Plan deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['platform-plans'] })
      // Refresh the plan picker used by the Add Company dialog (and the
      // AI Usage / Login Activity / Storage filters) — it caches plans
      // under a separate key, so without this it shows a stale list.
      void qc.invalidateQueries({ queryKey: ['platform-plans-dropdown'] })
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Stable identity so the downstream `useMemo`s only recompute when
  // the query actually returns a new payload, not on every render.
  const rows = useMemo(
    () => plansQuery.data?.data ?? [],
    [plansQuery.data],
  )

  // ── Apply search + chip filter. Empty search / 'all' chip leaves
  // the array untouched. Keeps the grid render branch dumb.
  //
  // Haystack covers everything the plan card visibly renders:
  //   • name / description / code — the identity fields
  //   • featuresList bullets ("SOPs + AI breakdown", "Workflows"…) —
  //     the only visible per-plan copy the user could reach for
  //     when nothing else jogs a name. Without these, searching for
  //     bullet text returned zero rows even though the plan was on
  //     screen (bug 0013136: "search by description returns no data"
  //     — the field the user actually saw was the feature bullet).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((p) => {
      if (filter === 'trial' && !planIsTrial(p)) return false
      if (filter === 'paid' && planIsTrial(p)) return false
      if (filter === 'inactive' && p.isActive !== false) return false
      if (q) {
        const hay = [
          p.name ?? '',
          p.description ?? '',
          p.code ?? '',
          ...(p.featuresList ?? []),
        ]
          .join(' ')
          .toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [rows, search, filter])

  // ── Aggregated stats for the strip beneath the page header.
  // Computed off the FILTERED set so the four KPI tiles update as
  // the operator narrows by search or chip — same pattern as the
  // Companies-page summary fix. When no filter is active `filtered`
  // === `rows` so the numbers match the full platform.
  const stats = useMemo(
    () => ({
      total: filtered.length,
      trial: filtered.filter(planIsTrial).length,
      paid: filtered.filter((p) => !planIsTrial(p)).length,
    }),
    [filtered],
  )

  // Only show the Monthly/Yearly toggle when a yearly price exists to
  // switch to — otherwise it's a no-op control.
  const anyYearly = useMemo(
    () =>
      rows.some(
        (p) =>
          p.yearlyPrice != null &&
          p.yearlyPrice !== '' &&
          Number(p.yearlyPrice) > 0,
      ),
    [rows],
  )

  return (
    <div>
      <Breadcrumb current="Plans" homePath="/super-admin/dashboard" />

      {/* Header — title in a soft accent tile next to the H1 so the
       *  page identity reads stronger than a small inline icon. */}
      {/* Stacks below `sm` so the action button is never pushed off the right
       *  edge on a phone — same header shape as Companies and Leads. */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] shrink-0">
            <Layers size={22} />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] leading-tight">
              Plans
            </h1>
            <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
              Manage subscription plans available to companies.
            </p>
          </div>
        </div>
        <Button className="shrink-0 self-start" onClick={() => setEditing({ __new: {} })}>
          <Plus size={14} />
          Create Plan
        </Button>
      </div>

      {/* Stats strip — at-a-glance overview of the plan catalogue.
       *  Always rendered (even on empty state) so the page rhythm
       *  doesn't jump when the admin creates the first plan. */}
      <StatsStrip stats={stats} loading={plansQuery.isLoading} />

      {/* Toolbar — shared FilterRow primitive. Hidden when the page is
       *  loading or there are no plans to filter, so the empty-state
       *  hero gets the full visual focus. */}
      {plansQuery.isLoading || rows.length === 0 ? null : (
        <div className="mt-5">
          <FilterRow>
            <FilterRow.Search
              value={search}
              onChange={setSearch}
              // Widened rather than abbreviated (QA DF_067). Measured, not assumed:
              // the app sets a 13px root font, so `w-96` is 24rem = 312px — NOT the
              // 384px a 16px root would give — and `pl-9`/`pr-9` are 29.25px each,
              // leaving 251.5px of usable width. This placeholder needs 323px at
              // 13px Inter, so it overflowed by 72px and the browser clipped it to
              // "…Code or Fe". Trimming the word "Plan" only got it to 293px, still
              // over, and made the placeholder name its own fields less clearly.
              // `w-[31rem]` = 403px leaves 342.5px usable — 19.5px of headroom, and
              // the row has ample space beside the single "All plans" select.
              width="w-full sm:w-[31rem]"
              placeholder="Search by Plan Name, Description, Code or Features"
            />
            <FilterRow.Select
              ariaLabel="Plan filter"
              placeholder="All plans"
              value={filter}
              onChange={setFilter}
              options={FILTER_CHIPS}
            />
            <FilterRow.Spacer />
            <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
            {/* Manual refresh to match Storage / AI Usage / Coupons — the
             *  plans query auto-refetches on window focus but not on
             *  demand, and admins editing plans across tabs otherwise had
             *  to full-page-refresh to see the update. */}
            <FilterRow.Refresh
              onClick={() => plansQuery.refetch()}
              loading={plansQuery.isFetching}
            />
          </FilterRow>
        </div>
      )}

      {anyYearly && !plansQuery.isLoading && !plansQuery.isError && rows.length > 0 ? (
        <div className="mt-4 inline-flex rounded-lg border border-[color:var(--color-rule)] p-0.5 bg-[color:var(--color-canvas)]">
          {['monthly', 'yearly'].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCadence(c)}
              className={
                'px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors cursor-pointer ' +
                (cadence === c
                  ? 'bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] shadow-sm'
                  : 'text-[color:var(--color-ink-muted)]')
              }
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}

      {/* Grid */}
      {plansQuery.isLoading ? (
        <SkeletonGrid />
      ) : plansQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(plansQuery.error).message}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={() => setEditing({ __new: {} })} />
      ) : filtered.length === 0 ? (
        <NoMatches
          onReset={() => {
            setSearch('')
            setFilter('all')
          }}
        />
      ) : (
        // All plans shown at once — a wrapping grid (matches SkeletonGrid
        // above) so admins can compare every tier without paging through a
        // carousel. `pt-4` leaves room for the "Most Popular" ribbon.
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 pt-4">
          {filtered.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              cadence={cadence}
              onEdit={() => setEditing(p)}
              onDuplicate={() => setEditing({ __duplicate: p })}
              onDelete={() => setConfirmDelete(p)}
            />
          ))}
        </div>
      )}

      {editing ? (
        <PlanDialog
          initial={editing.__duplicate ?? (editing.__new ? null : editing)}
          duplicate={Boolean(editing.__duplicate)}
          template={editing.__new ?? null}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Plan"
        message={
          confirmDelete
            ? `Delete plan "${confirmDelete.name}"? ${
                confirmDelete.activeCompanyCount > 0
                  ? `This plan currently has ${confirmDelete.activeCompanyCount} active compan${confirmDelete.activeCompanyCount === 1 ? 'y' : 'ies'} — migrate them to a different plan first. Delete is disabled until this plan has no active companies.`
                  : 'This action cannot be undone.'
              }`
            : ''
        }
        confirmLabel="Delete Plan"
        variant="danger"
        confirming={removeMutation.isPending}
        // Disable the Delete button when the plan still has active tenants
        // — the server would refuse the call anyway, so the click was pure
        // dead weight. The message above explains why.
        confirmDisabled={
          confirmDelete ? confirmDelete.activeCompanyCount > 0 : false
        }
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) removeMutation.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

/** Stats strip — four KPI tiles. Each tile has a coloured icon chip
 *  and a tabular-nums number so the figures line up nicely. */
/** KPI strip for the Plans page — visually matches the Companies-
 *  page strip (rounded-xl chrome, halo bleed, 4xl font-display
 *  number, 12×12 tinted icon chip on the right). Plans don't carry
 *  a 14-day time-series the way Tenants do, so the sparkline prop
 *  is intentionally omitted on these cards. KpiCard handles the
 *  missing prop gracefully (no curve renders). */
function StatsStrip({ stats, loading }) {
  // The "Active Organisations" tile was removed (bug 0011519 — flagged
  // as non-useful by the operator: the per-plan "N active" chip on each
  // card already carries the same signal at higher granularity, so the
  // aggregate tile was pure duplication). Grid dropped from 4→3 columns.
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <KpiCard
        label="Total Plans"
        value={stats.total}
        icon={Layers}
        tone="violet"
        loading={loading}
      />
      <KpiCard
        label="Trial Plans"
        value={stats.trial}
        icon={Sparkles}
        tone="amber"
        loading={loading}
      />
      <KpiCard
        label="Paid Plans"
        value={stats.paid}
        icon={TrendingUp}
        tone="sky"
        loading={loading}
      />
    </div>
  )
}

/** Per-tone tile + halo bleed for the Plans-page KPI strip. Lifted
 *  from `TenantsPage.jsx` so the Plans + Companies pages share the
 *  exact same KPI chrome. Five tones available; the four cards
 *  here use violet / emerald / amber / sky to match the four
 *  metrics conceptually (catalogue size / customer count / trial
 *  subset / paid subset). */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the Companies-page `KpiCard` shape
 *  beat-for-beat — rounded-xl chrome, blurred halo bleed in the
 *  top-right, 12×12 tinted icon chip, 4xl font-display value,
 *  uppercase tracking-wider label. Skeleton state during the
 *  initial fetch swaps the number for a pulsing placeholder so the
 *  strip doesn't pop in. */
function KpiCard({ label, value, icon: Icon, tone = 'violet', loading }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-card)] p-14 text-center">
      <span className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
        <Layers size={26} />
      </span>
      <p className="text-base font-semibold text-[color:var(--color-ink-strong)]">
        No plans yet
      </p>
      <p className="mt-1.5 text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto leading-relaxed">
        Create the first plan so new tenants can pick one during sign-up
        and existing tenants get the right resource caps + feature
        bundle.
      </p>
      <Button onClick={onCreate} className="mt-5">
        <Plus size={14} />
        Create the First Plan
      </Button>
    </div>
  )
}

/** Rendered when the search / filter combo yields no rows but plans
 *  do exist — distinguishes "you typed something" from "no plans at
 *  all" so the empty state stays useful. */
function NoMatches({ onReset }) {
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center">
      <Search
        size={22}
        className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
      />
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        No plans match your filter
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
        Try a different search term or clear the chip filter.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-3 inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm font-medium text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-bg)] cursor-pointer"
      >
        Clear Filters
      </button>
    </div>
  )
}

/** Skeleton grid shown while the first /plans fetch is in flight.
 *  Six placeholder cards so the layout settles immediately instead
 *  of "Loading…" → grid-jump on resolve. */
function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 h-80 animate-pulse"
        >
          <div className="h-4 w-24 rounded bg-[color:var(--color-rule)] mb-3" />
          <div className="h-8 w-32 rounded bg-[color:var(--color-rule)] mx-auto mb-4" />
          <div className="h-20 rounded bg-[color:var(--color-rule)] mb-4" />
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-[color:var(--color-rule)]" />
            <div className="h-3 w-5/6 rounded bg-[color:var(--color-rule)]" />
            <div className="h-3 w-2/3 rounded bg-[color:var(--color-rule)]" />
          </div>
        </div>
      ))}
    </div>
  )
}

function PlanCard({ plan, cadence = 'monthly', onEdit, onDuplicate, onDelete }) {
  // Pricing-page layout matching the Sign-up + Billing cards: name →
  // price → description → boxed caps → action row → feature ticks →
  // admin footer (active-company count + Default / Inactive / Private
  // badges). The Featured (default) plan gets a thicker accent border; the
  // "Most Popular" pill is admin-toggled.
  const isFeatured = Boolean(plan.isDefault)
  const features = Array.isArray(plan.featuresList) ? plan.featuresList : []

  return (
    <div
      className={
        'group relative rounded-2xl bg-[color:var(--color-card)] p-6 flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-md ' +
        (isFeatured
          ? 'border border-[color:var(--color-accent-strong)] shadow-sm'
          : 'border border-[color:var(--color-rule)]')
      }
    >
      {/* "Most Popular" ribbon — admin-toggled, straddles the card's
       *  top border, centered. */}
      {plan.isPopular ? (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--color-accent-strong)] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white shadow-sm whitespace-nowrap">
          <Sparkles size={12} strokeWidth={2.5} />
          Most Popular
        </span>
      ) : null}

      {/* Draft pill. It sits here rather than in the footer badge group
        *  because a draft is a state of the WHOLE card — the plan is not
        *  live yet — so it belongs before the name, not after the feature
        *  list. The "Most Popular" ribbon owns the centre of the top
        *  border, so nothing else may sit there without colliding with it.
        *
        *  "Default" used to sit here too; it now rides in the footer badge
        *  group beside Inactive/Private, which is where the card already
        *  collects its admin-facing status flags. */}
      {plan.isDraft ? (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-ink-muted)]">
            Draft
          </span>
        </div>
      ) : null}

      {/* `break-words`/`overflow-wrap:anywhere` — a plan name may be up to
        *  100 characters (see the DTO) and the charset allows a single
        *  unbroken run, which has no break opportunity. The card sets no
        *  overflow rule, so the heading spilled OUT of the card and painted
        *  over the neighbouring cards in the grid (QA, 2026-09-01). The grid
        *  columns are minmax(0,1fr) and never widened — it was the heading
        *  escaping its own card. */}
      <h3 className="font-display text-xl font-semibold tracking-tight text-[color:var(--color-ink-strong)] break-words [overflow-wrap:anywhere]">
        {plan.name}
      </h3>

      {/* Price — large value + small inline period suffix. */}
      <div className="mt-3">
        <PriceBlock
          type={plan.type}
          isTrial={planIsTrial(plan)}
          monthlyPrice={plan.monthlyPrice}
          yearlyPrice={plan.yearlyPrice}
          cadence={cadence}
          currency={plan.currency}
          trialDays={plan.trialDays}
        />
      </div>

      {plan.description ? (
        // Same wrap rule as the plan name above — a description can be up to
        // 280 characters and nothing forces a break opportunity.
        <p className="mt-3 text-sm text-[color:var(--color-ink-muted)] leading-snug break-words [overflow-wrap:anywhere]">
          {plan.description}
        </p>
      ) : null}

      <CapBox plan={plan} />

      {/* Edit / Delete actions — the card's "CTA" row, sitting under
       *  the caps box where the customer cards put their action button.
       *  `flex-nowrap` + `min-w-0`/`truncate` keep Edit + Delete on one
       *  line even on the narrowest card. */}
      <div className="mt-5 flex flex-nowrap items-center gap-2">
        <button
          type="button"
          onClick={onEdit}
          title="Edit Plan"
          aria-label="Edit plan"
          className="taskmagix-btn-primary flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap"
        >
          <Pencil size={14} className="shrink-0" />
          <span className="truncate">Edit Plan</span>
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          title="Duplicate Plan"
          aria-label="Duplicate plan"
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] hover:text-[color:var(--color-accent-strong)] hover:border-[color:var(--color-accent-border)] hover:bg-[color:var(--color-accent-bg)] cursor-pointer transition-colors"
        >
          <Copy size={15} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete Plan"
          aria-label="Delete plan"
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] hover:text-[color:var(--color-priority-urgent)] hover:border-[color:var(--color-danger-border)] hover:bg-[color:var(--color-danger-bg)] cursor-pointer transition-colors"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {/* Feature ticks — the plan's marketed features (caps now live in
       *  the boxed section above, so this is just the featuresList). */}
      {features.length > 0 ? (
        <ul className="mt-5 mb-8 space-y-2.5 text-sm">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check
                size={15}
                strokeWidth={2.5}
                className="text-[color:var(--color-success)] shrink-0 mt-0.5"
              />
              <span className="text-[color:var(--color-ink)] leading-snug">
                {f}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Bottom strip — active-company count + admin status badges
       *  (Inactive / Private). Pushed to the foot of the card with
       *  mt-auto so siblings in the same row keep their baselines
       *  aligned regardless of feature-list length. The active-
       *  count line uses `text-sm` (was text-xs) and a 14px icon
       *  (was 12px) so the number reads as a meaningful KPI at the
       *  bottom of the card rather than fine print. */}
      <div className="mt-auto pt-6 border-t border-[color:var(--color-rule)] flex items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1.5 text-sm text-[color:var(--color-ink-muted)]">
          <Building2
            size={14}
            className="text-[color:var(--color-ink-subtle)]"
          />
          <span>
            <span className="font-semibold text-[color:var(--color-ink-strong)] tabular-nums text-base">
              {plan.activeCompanyCount ?? 0}
            </span>{' '}
            active
          </span>
        </p>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {/* "Inactive", not "Hidden" — the same word the status filter and
            *  the editor's Active toggle use for this exact flag. It read
            *  "Hidden" before, which was inconsistent with both AND collided
            *  with the Private badge beside it: two visibility-sounding words
            *  for two different axes, on/off versus public/private
            *  (QA, 2026-09-01). */}
          {!plan.isActive ? (
            <span
              className="inline-flex w-fit items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
              title="Switched off — cannot be assigned or subscribed to."
            >
              Inactive
            </span>
          ) : null}
          {plan.isActive && plan.isPublic === false ? (
            <span
              className="inline-flex w-fit items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
              title="Not shown on the public Sign-up picker. Only assignable by Master Admin."
            >
              Private
            </span>
          ) : null}
          {/* Default — last in the group so it lands at the right edge of
            *  the footer. Styled like its siblings (same 10px uppercase
            *  chip) so the group reads as one row of status flags, but
            *  keeps the accent palette so it stays distinguishable from the
            *  muted Inactive and the amber Private.
            *
            *  "Default" was signalled by the accent border ALONE before
            *  2026-09-01 — the only one of the three states with nothing to
            *  read. The border stays; this names it. `title` carries what
            *  "default" actually means, which the border could never
            *  convey. */}
          {isFeatured ? (
            <span
              title="New companies are assigned this plan at sign-up."
              className="inline-flex w-fit items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]"
            >
              Default
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ── Create / Edit modal ──────────────────────────────────────────────

// Curated catalogue of plan features surfaced as a checkbox grid in the
// Plan dialog. Ticking one does TWO things:
//   1. adds it as a green-tick bullet on the plan card (`featuresList`), and
//   2. enables the matching capability flag in `Plan.features` (`key`), which
//      the backend `requireFeature(key)` guards + the SPA's `hasFeature`
//      gate read to unlock/hide the module per tenant.
//
// `key` MUST match backend `config/plan-features.js` + `tenantFeatures`
// (lib/types.js). Entries with `key: null` are display-only (e.g. "Email
// support" — not a software capability). Anything not in this list goes in
// the free-text "Custom lines" box, so cap-derived bullets ("Up to 50
// members") and meta lines ("Everything in Starter and:") stay expressible.
const FEATURE_CATALOGUE = [
  // Capability checkboxes derive from the feature registry (single source of
  // truth). `{ key: null }` rows are display-only marketing lines.
  ...planCardFeatures(),
  { key: null, label: 'Email support' },
]

/** Decide which catalogue boxes are ticked + which free-text lines remain,
 *  for an existing plan. A box ticks when EITHER its bullet is present in
 *  `featuresList` (case-insensitive) OR its capability flag is explicitly
 *  `true` in `features` (so a plan that enabled a capability without a
 *  matching bullet still shows ticked). Selection is tracked by label so
 *  it doubles as the card-bullet set. Non-catalogue lines (caps, promos,
 *  "Everything in X") fall through to the custom textarea — nothing an
 *  admin previously typed is ever dropped. */
function splitFeatures(list, features) {
  const all = Array.isArray(list)
    ? list.map((s) => String(s).trim()).filter(Boolean)
    : []
  const labelByLower = new Map(
    FEATURE_CATALOGUE.map((f) => [f.label.toLowerCase(), f.label]),
  )
  // Legacy-label migration: plans saved before "API access" was renamed to
  // "Bring Your Own AI keys" stored the old bullet text. Map it to the current
  // label so an existing plan stays ticked and the stale bullet is absorbed
  // (not duplicated) the next time the plan is saved.
  const apiAccessLabel = FEATURE_CATALOGUE.find((f) => f.key === 'api_access')?.label
  if (apiAccessLabel) labelByLower.set('api access', apiAccessLabel)
  const selected = []
  const seen = new Set()
  const custom = []
  for (const line of all) {
    const canonical = labelByLower.get(line.toLowerCase())
    if (canonical && !seen.has(canonical)) {
      selected.push(canonical)
      seen.add(canonical)
    } else {
      custom.push(line)
    }
  }
  if (features && typeof features === 'object') {
    for (const f of FEATURE_CATALOGUE) {
      if (f.key && features[f.key] === true && !seen.has(f.label)) {
        selected.push(f.label)
        seen.add(f.label)
      }
    }
  }
  return { selected, customText: custom.join('\n') }
}

const EMPTY = {
  name: '',
  type: 'paid',
  trialDays: '',
  // A paid plan can be priced monthly, yearly, or BOTH — each is an
  // independent optional field. Blank = that cycle isn't offered; both
  // blank = "Custom". Routed straight to the model's monthlyPrice /
  // yearlyPrice on save.
  monthlyPrice: '',
  yearlyPrice: '',
  currency: 'USD',
  // The four caps that actually show on the plan card. The other
  // engine-enforced caps (max tasks, SOPs, documents, API keys)
  // live in billing/limits.js with sensible per-tier defaults —
  // not admin-tunable through this form.
  maxUsers: '',
  maxWorkspaces: '',
  storageGb: '',
  aiTokensPerMonth: '',
  // Highlighted-feature picker state. `selectedFeatures` holds the
  // ticked catalogue labels; `featuresText` holds free-text custom
  // bullets. They're merged into a single `featuresList` on submit.
  selectedFeatures: [],
  featuresText: '',
  // Single in-code feature flag — toggles the Integrations sidebar
  // item in the Company Admin panel. Other "features" are marketed
  // via the bullets above. New gateable flags get added here one at a
  // time as they're wired into actual code.
  sopIntegration: false,
  isActive: true,
  isPublic: true,
  isPopular: false,
  isDefault: false,
  isContactSales: false,
  sortOrder: 0,
}

/** Block keystrokes that would introduce a negative number (or
 *  exponent) into a numeric plan field — prices, caps, counts and the
 *  sort index are all non-negative. Paired with `min={0}` (floors the
 *  spinner) and `stripNegative` on change (sanitizes paste). */
function blockNegativeKey(e) {
  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
    e.preventDefault()
  }
}

/** Strip any minus sign so a pasted/autofilled "-50" becomes "50" — the
 *  backstop for the keydown guard, which can't intercept paste. */
function stripNegative(v) {
  return typeof v === 'string' ? v.replace(/-/g, '') : v
}

/** Blank ('' / null) → null (Unlimited); a finite number otherwise. Mirrors
 *  the submit's intOrNull so the preview caps match what will be saved. */
function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** The exact featuresList the form will submit — custom lines first (in the
 *  order typed), then ticked catalogue labels not already typed. Kept byte-
 *  identical to the saveMutation body-builder so the preview never lies. */
function previewBullets(form) {
  const customLines = form.featuresText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  const customLower = new Set(customLines.map((s) => s.toLowerCase()))
  const ticked = FEATURE_CATALOGUE.filter(
    (f) =>
      form.selectedFeatures.includes(f.label) &&
      !customLower.has(f.label.toLowerCase()),
  ).map((f) => f.label)
  return [...customLines, ...ticked]
}

/** Live preview of the plan card a customer will see. Reads straight from the
 *  editor's form state and reuses the same PriceBlock + CapBox the real cards
 *  use, so what the admin sees is what customers get. */
function PlanPreviewCard({ form }) {
  const isTrial = form.type === 'trial'
  const caps = {
    maxUsers: numOrNull(form.maxUsers),
    maxWorkspaces: numOrNull(form.maxWorkspaces),
    storageGb: numOrNull(form.storageGb),
    aiTokensPerMonth: numOrNull(form.aiTokensPerMonth),
  }
  const bullets = previewBullets(form)
  return (
    <div className="rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 shadow-sm overflow-hidden">
      {form.isPopular ? (
        <span className="mb-2 inline-flex items-center rounded-full border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-2.5 py-0.5 text-[11px] font-semibold text-[color:var(--color-accent-strong)]">
          Most Popular
        </span>
      ) : null}
      {/* Same wrap rule as the real card. This one has `overflow-hidden`,
        *  so it clipped the name rather than overlapping — still wrong, and
        *  a preview that cuts the name off misrepresents the card it is
        *  previewing. */}
      <h3 className="font-display text-lg font-semibold text-[color:var(--color-ink-strong)] leading-tight break-words [overflow-wrap:anywhere]">
        {form.name.trim() || 'Plan name'}
      </h3>
      <div className="mt-2">
        <PriceBlock
          type={form.type}
          monthlyPrice={isTrial ? 0 : form.monthlyPrice === '' ? null : form.monthlyPrice}
          yearlyPrice={isTrial ? null : form.yearlyPrice === '' ? null : form.yearlyPrice}
          currency={form.currency}
          trialDays={form.trialDays}
        />
      </div>
      <CapBox plan={caps} />
      {bullets.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {bullets.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-[color:var(--color-ink)]"
            >
              <Check
                size={15}
                className="mt-0.5 shrink-0 text-[color:var(--color-accent-strong)]"
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Pre-flight checklist for the final step — a plain-language summary of what
 *  the plan will do once live, with a soft note on the easy-to-miss gaps.
 *  Purely informational; reads straight from the form. */
function PreflightChecklist({ form }) {
  const isTrial = form.type === 'trial'
  const rows = []
  if (isTrial) {
    rows.push({
      note: !form.trialDays,
      label: form.trialDays ? `${form.trialDays}-day free trial` : 'Trial length not set',
    })
  } else if (form.monthlyPrice === '' && form.yearlyPrice === '') {
    rows.push({ note: true, label: 'No price set — the card shows “Custom”' })
  } else if (Number(form.monthlyPrice) === 0 && form.yearlyPrice === '') {
    rows.push({ note: false, label: 'Free forever' })
  } else {
    const cycles = []
    if (form.monthlyPrice !== '' && Number(form.monthlyPrice) > 0) cycles.push('per month')
    if (form.yearlyPrice !== '' && Number(form.yearlyPrice) > 0) cycles.push('per year')
    rows.push({
      note: false,
      label: cycles.length ? `Priced ${cycles.join(' and ')}` : 'Free forever',
    })
  }
  rows.push({
    note: false,
    label: form.isPublic ? 'Shown on the Sign-up page' : 'Private — admin-assigned only',
  })
  const bulletCount = previewBullets(form).length
  rows.push({
    note: bulletCount === 0,
    label:
      bulletCount > 0
        ? `${bulletCount} feature line${bulletCount === 1 ? '' : 's'} on the card`
        : 'No feature lines on the card yet',
  })
  return (
    <div className="pt-2 border-t border-[color:var(--color-rule)]">
      <FieldLabel label="Before You Publish" />
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center gap-1.5 text-sm">
            {r.note ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-[color:var(--color-priority-normal)]"
                aria-hidden="true"
              />
            ) : (
              <Check size={15} className="shrink-0 text-[color:var(--color-accent-strong)]" />
            )}
            <span
              className={
                r.note
                  ? 'text-[color:var(--color-ink-muted)]'
                  : 'text-[color:var(--color-ink)]'
              }
            >
              {r.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PlanDialog({ initial, duplicate = false, template = null, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(() => {
    if (!initial) return { ...EMPTY, ...(template ?? {}) }
    const { selected, customText } = splitFeatures(
      initial.featuresList,
      initial.features,
    )
    return {
      name: duplicate ? '' : (initial.name ?? ''),
      type: initial.type ?? 'paid',
      trialDays: initial.trialDays != null ? String(initial.trialDays) : '',
      // Monthly and yearly are independent — load each straight from the
      // plan so an existing price on either cycle is preserved on edit.
      monthlyPrice: initial.monthlyPrice != null ? String(initial.monthlyPrice) : '',
      yearlyPrice: initial.yearlyPrice != null ? String(initial.yearlyPrice) : '',
      currency: initial.currency ?? 'USD',
      maxUsers: initial.maxUsers != null ? String(initial.maxUsers) : '',
      maxWorkspaces:
        initial.maxWorkspaces != null ? String(initial.maxWorkspaces) : '',
      storageGb:
        initial.storageGb != null ? String(initial.storageGb) : '',
      aiTokensPerMonth:
        initial.aiTokensPerMonth != null
          ? String(initial.aiTokensPerMonth)
          : '',
      selectedFeatures: selected,
      featuresText: customText,
      sopIntegration: Boolean(initial.features?.sop_integration),
      isActive: initial.isActive ?? true,
      isPublic: initial.isPublic ?? true,
      isPopular: initial.isPopular ?? false,
      isDefault: initial.isDefault ?? false,
      isContactSales: initial.isContactSales ?? false,
      sortOrder: initial.sortOrder ?? 0,
    }
  })
  const [errors, setErrors] = useState({})
  // Two-step wizard: 1 = Basics & pricing, 2 = Features & visibility.
  const [step, setStep] = useState(1)

  const isEdit = Boolean(initial) && !duplicate
  const isTrial = form.type === 'trial'
  // New (and duplicated) plans are born as drafts; editing a live plan keeps
  // it published unless the admin re-drafts it. Drives the footer actions.
  const isDraftPlan = isEdit ? Boolean(initial.isDraft) : true

  const saveMutation = useMutation({
    mutationFn: async (opts = {}) => {
      const intOrNull = (v) => {
        if (v === '' || v === null || v === undefined) return null
        const n = Number(v)
        return Number.isFinite(n) ? Math.trunc(n) : null
      }
      const priceOrNull = (v) => {
        if (v === '' || v === null || v === undefined) return null
        const n = Number(v)
        return Number.isFinite(n) ? n : null
      }
      const body = {
        name: form.name.trim(),
        type: form.type,
        // Trial plans send monthlyPrice=0 and trialDays>0. Paid plans send
        // monthly and yearly INDEPENDENTLY — either, both, or neither
        // (neither → "Custom"). The backend `normaliseTypeFields()` enforces
        // the trial shape defensively but doing it here keeps the payload tidy.
        monthlyPrice: isTrial ? 0 : priceOrNull(form.monthlyPrice),
        yearlyPrice: isTrial ? null : priceOrNull(form.yearlyPrice),
        currency: form.currency,
        trialDays: isTrial ? intOrNull(form.trialDays) : null,
        maxUsers: intOrNull(form.maxUsers),
        maxWorkspaces: intOrNull(form.maxWorkspaces),
        storageGb: intOrNull(form.storageGb),
        aiTokensPerMonth: intOrNull(form.aiTokensPerMonth),
        // Highlighted features = free-text custom lines first (caps /
        // promos, in the order typed) followed by the ticked catalogue
        // features in catalogue order. Custom lines win on a clash so
        // the card never shows the same bullet twice — a matching
        // catalogue tick is suppressed if the admin also typed it.
        featuresList: (() => {
          const customLines = form.featuresText
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
          const customLower = new Set(customLines.map((s) => s.toLowerCase()))
          const tickedLabels = FEATURE_CATALOGUE.filter(
            (f) =>
              form.selectedFeatures.includes(f.label) &&
              !customLower.has(f.label.toLowerCase()),
          ).map((f) => f.label)
          return [...customLines, ...tickedLabels]
        })(),
        // Capability flags blob — drives per-tenant feature gating.
        // `sop_integration` is the legacy ProcessPro connector; the rest
        // are the plan-gated capabilities. Every keyed catalogue feature
        // writes an EXPLICIT boolean (ticked → true, unticked → false) so
        // a new/edited plan that omits a feature restricts it. "Email
        // support" (key: null) is display-only and contributes no flag.
        features: (() => {
          const flags = { sop_integration: Boolean(form.sopIntegration) }
          for (const f of FEATURE_CATALOGUE) {
            if (f.key) flags[f.key] = form.selectedFeatures.includes(f.label)
          }
          return flags
        })(),
        isActive: form.isActive,
        isDraft: Boolean(opts.isDraft),
        isPublic: form.isPublic,
        isPopular: form.isPopular,
        isDefault: form.isDefault,
        isContactSales: form.isContactSales,
        sortOrder: Number(form.sortOrder) || 0,
      }
      if (isEdit) {
        const res = await api.patch(`/super-admin/plans/${initial.id}`, body)
        return res.data
      }
      const res = await api.post('/super-admin/plans', body)
      return res.data
    },
    onSuccess: async () => {
      toast.success(isEdit ? 'Plan updated successfully.' : 'Plan created successfully.')
      // Await the Plans-list refetch BEFORE closing the dialog so the new /
      // updated card is on screen the instant it closes. Previously this was
      // a fire-and-forget `invalidateQueries` immediately followed by
      // `onClose()` — the close could win the race and leave the grid showing
      // the pre-save list until a manual page refresh.
      await qc.invalidateQueries({ queryKey: ['platform-plans'] })
      // Secondary caches (the plan picker in the Add Company dialog + the
      // AI Usage / Login Activity / Storage filters) cache plans under a
      // separate key. They don't gate the close, so fire-and-forget is fine.
      void qc.invalidateQueries({ queryKey: ['platform-plans-dropdown'] })
      void qc.invalidateQueries({ queryKey: ['platform-tenants'] })
      onClose()
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) setErrors(fields)
      else toast.error(message)
    },
  })

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) {
      const { [key]: _omit, ...rest } = errors
      void _omit
      setErrors(rest)
    }
  }

  /** Toggle a catalogue feature in/out of the ticked set. */
  function toggleFeature(label) {
    setForm((f) => ({
      ...f,
      selectedFeatures: f.selectedFeatures.includes(label)
        ? f.selectedFeatures.filter((x) => x !== label)
        : [...f.selectedFeatures, label],
    }))
  }

  /** Validate, then save with an explicit draft flag — Publish = false,
   *  Save as draft = true. Any validation error jumps back to step 1. */
  function doSave(asDraft) {
    if (saveMutation.isPending) return
    const next = validate()
    setErrors(next)
    if (Object.keys(next).length > 0) {
      setStep(1)
      return
    }
    saveMutation.mutate({ isDraft: asDraft })
  }

  /** Run client-side validation, return a per-field errors object.
   *  Empty object means the form is valid. The submit handler calls
   *  this on click — never gates the button — so the user always sees
   *  WHY a submit was rejected instead of staring at a greyed-out
   *  button with no feedback. */
  function validate() {
    const next = {}
    const name = form.name.trim()
    if (!name) {
      next.name = 'Plan Name is required.'
    } else if (name.length < 2) {
      next.name = 'Plan Name must be at least 2 characters.'
    } else if (name.length > 100) {
      next.name = 'Plan Name must be at most 100 characters.'
    } else if (!/^[A-Za-z0-9 ]+$/.test(name)) {
      next.name = 'Plan Name must be letters, numbers and spaces only.'
    } else if (!/[A-Za-z]/.test(name)) {
      next.name = 'Plan Name must include at least one letter.'
    }
    if (isTrial) {
      const td = Number(form.trialDays)
      if (!form.trialDays) {
        next.trialDays = 'Trial Days is required for trial plans.'
      } else if (!Number.isFinite(td) || td < 1) {
        next.trialDays = 'Trial Days must be at least 1.'
      } else if (td > 365) {
        next.trialDays = 'Trial Days must be at most 365.'
      }
    }
    // Monthly and yearly are independent optional fields — validate each
    // when supplied (non-negative, within the ceiling).
    for (const key of ['monthlyPrice', 'yearlyPrice']) {
      if (!isTrial && form[key] !== '') {
        const p = Number(form[key])
        if (!Number.isFinite(p) || p < 0) {
          next[key] = 'Price must be a non-negative number.'
        } else if (p > 100_000_000) {
          next[key] = 'Price must be at most 100,000,000.'
        }
      }
    }
    // Cap fields are optional (blank = unlimited). When supplied they must
    // be non-negative integers within a sane upper bound — the ceilings
    // stop a mis-key like 10^32 from becoming plan data (and from blowing
    // the Live Preview layout wide). All are far above any real plan.
    const CAP_MAX = {
      maxUsers: 1_000_000,
      maxWorkspaces: 100_000,
      storageGb: 1_000_000,
      aiTokensPerMonth: 1_000_000_000,
    }
    for (const key of [
      'maxUsers',
      'maxWorkspaces',
      'storageGb',
      'aiTokensPerMonth',
    ]) {
      if (form[key] !== '' && form[key] !== null && form[key] !== undefined) {
        const n = Number(form[key])
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
          next[key] = 'Must be a non-negative whole number.'
        } else if (n > CAP_MAX[key]) {
          next[key] = `Must be at most ${formatNumber(CAP_MAX[key])}.`
        }
      }
    }
    return next
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Plan' : duplicate ? 'Duplicate Plan' : 'Add Plan'}
      description={
        isEdit
          ? `Update details for the ${initial?.name ?? ''} plan.`
          : duplicate
            ? `A new plan pre-filled from “${initial?.name ?? ''}”. Give it a name to save.`
            : 'The pricing plan customers see during Sign-up.'
      }
      widthClass="max-w-6xl"
      footer={
        step === 1 ? (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button form="plan-form" type="submit">
              Next
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => setStep(1)}
              disabled={saveMutation.isPending}
            >
              Back
            </Button>
            {isDraftPlan ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => doSave(true)}
                  loading={saveMutation.isPending}
                >
                  Save as Draft
                </Button>
                <Button
                  onClick={() => doSave(false)}
                  loading={saveMutation.isPending}
                >
                  Publish Plan
                </Button>
              </>
            ) : (
              <Button
                onClick={() => doSave(false)}
                loading={saveMutation.isPending}
              >
                Save Changes
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col lg:flex-row gap-6">
      <form
        id="plan-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (saveMutation.isPending) return
          // All validated fields live on step 1, so validate then either
          // advance to step 2 or submit. Any error jumps back to step 1
          // where the offending field is visible. Also gives Enter-to-
          // advance for free.
          const next = validate()
          setErrors(next)
          if (Object.keys(next).length > 0) {
            setStep(1)
            return
          }
          if (step === 1) {
            setStep(2)
            return
          }
          // Step 2 is the final step; the footer's Publish / Save-as-draft
          // buttons perform the save (each with its own isDraft), so an
          // Enter keypress here just no-ops.
        }}
        noValidate
        className="space-y-3 flex-1 min-w-0"
      >
        <div className={step === 1 ? 'space-y-4' : 'hidden'}>
        {/* Plan Name + Plan Type share a row so the form doesn't waste
         *  two full-width stripes on short fields. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* `showCount` renders the live "0/100" counter from the moment
            *  the dialog opens, so the cap is knowable BEFORE it is hit —
            *  the field previously said nothing until the admin was already
            *  over it (QA, 2026-09-01). Same treatment Profile, Departments
            *  and Work Roles already use on their capped text fields. */}
          <TextField
            label="Plan Name"
            required
            placeholder="Enter a Plan Name"
            minLength={2}
            maxLength={100}
            showCount
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={errors.name}
          />
          <div>
            <FieldLabel
              label="Plan Type"
              required
              htmlFor="plan-type"
            />
            <select
              id="plan-type"
              value={form.type}
              onChange={(e) => setField('type', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Pricing row. Trial → [Trial Days | Max Users]. Paid → a
         *  [Billing Period | Price | Max Users] trio: the dropdown picks
         *  the cadence and the single Price field is that period's price.
         *  "Unlimited" placeholder doubles as the affordance for the caps. */}
        {isTrial ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField
              label="Trial Days"
              required
              type="number"
              inputMode="numeric"
              min={1}
              max={365}
              onKeyDown={blockNegativeKey}
              placeholder="Enter the Trial Days"
              value={form.trialDays}
              onChange={(e) => setField('trialDays', stripNegative(e.target.value))}
              error={errors.trialDays}
            />
            <TextField
              label="Max Users"
              type="number"
              inputMode="numeric"
              min={0}
              onKeyDown={blockNegativeKey}
              placeholder="Unlimited"
              value={form.maxUsers}
              onChange={(e) => setField('maxUsers', stripNegative(e.target.value))}
              error={errors.maxUsers}
            />
          </div>
        ) : (
          <div className="space-y-2">
            {/* A paid plan can carry a monthly price, a yearly price, or
             *  BOTH — set independently, in the shared currency. Blank = that
             *  cycle isn't offered; both blank = "Custom". */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <FieldLabel label="Currency" htmlFor="plan-currency" />
                <select
                  id="plan-currency"
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
                >
                  {CURRENCY_CODES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              {[
                { key: 'monthlyPrice', label: 'Monthly price', id: 'plan-price-monthly' },
                { key: 'yearlyPrice', label: 'Yearly price', id: 'plan-price-yearly' },
              ].map(({ key, label, id }) => (
                <div key={key}>
                  <FieldLabel label={label} htmlFor={id} />
                  <div
                    className={
                      'flex h-10 rounded-lg border bg-[color:var(--color-card)] ' +
                      'transition-colors focus-within:border-[color:var(--color-accent)] overflow-hidden ' +
                      (errors[key]
                        ? 'border-[color:var(--color-priority-urgent)]'
                        : 'border-[color:var(--color-rule)]')
                    }
                  >
                    <span className="inline-flex items-center px-2.5 border-r border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink-muted)]">
                      {form.currency}
                    </span>
                    <input
                      id={id}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min={0}
                      onKeyDown={blockNegativeKey}
                      placeholder="Enter the price"
                      value={form[key]}
                      onChange={(e) => setField(key, stripNegative(e.target.value))}
                      aria-invalid={Boolean(errors[key])}
                      className="w-full min-w-0 h-full px-3 bg-transparent text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] outline-none"
                    />
                  </div>
                  {errors[key] ? (
                    <p
                      role="alert"
                      className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
                    >
                      {errors[key]}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              Set either or both cycles · blank = that cycle isn’t offered · 0
              in Monthly = Forever free · both blank = Custom
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!isTrial ? (
            <TextField
              label="Max Users"
              type="number"
              inputMode="numeric"
              min={0}
              onKeyDown={blockNegativeKey}
              placeholder="Unlimited"
              value={form.maxUsers}
              onChange={(e) => setField('maxUsers', stripNegative(e.target.value))}
              error={errors.maxUsers}
            />
          ) : null}
          <TextField
            label="Storage (GB)"
            type="number"
            inputMode="numeric"
            min={0}
            onKeyDown={blockNegativeKey}
            placeholder="Unlimited"
            value={form.storageGb}
            onChange={(e) => setField('storageGb', stripNegative(e.target.value))}
            error={errors.storageGb}
          />
          <TextField
            label="AI Tokens / Month"
            type="number"
            inputMode="numeric"
            min={0}
            onKeyDown={blockNegativeKey}
            placeholder="Unlimited"
            value={form.aiTokensPerMonth}
            onChange={(e) => setField('aiTokensPerMonth', stripNegative(e.target.value))}
            error={errors.aiTokensPerMonth}
          />
        </div>
        </div>

        <div className={step === 2 ? 'space-y-3' : 'hidden'}>
        {/* Plan features — checkbox picker. Ticking a keyed feature both
         *  shows it as a green-tick row on the card AND unlocks that
         *  module for organisations on this plan (backend
         *  `requireFeature` + SPA `hasFeature` gate). Unticked keyed
         *  features are blocked. "Email support" is display-only. The
         *  free-text box below covers non-catalogue bullets (cap-derived
         *  lines like "Up to 50 members", promos, "Everything in X"). */}
        <div>
          <FieldLabel label="Plan Features" />
          <p className="-mt-1 mb-2 text-xs text-[color:var(--color-ink-muted)]">
            Tick the features included in this plan. Each shows as a
            green-tick row on the card and unlocks that module for
            companies on this plan.
          </p>
          {/* One bordered container holding every checkbox (rather
           *  than a separate bordered tile per feature), laid out in a
           *  two-column grid inside it. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-3.5">
            {FEATURE_CATALOGUE.map((f) => {
              const checked = form.selectedFeatures.includes(f.label)
              return (
                <label
                  key={f.label}
                  className="flex items-center gap-2.5 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFeature(f.label)}
                    className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)] shrink-0"
                  />
                  <span className="text-sm text-[color:var(--color-ink-strong)]">
                    {f.label}
                  </span>
                </label>
              )
            })}
          </div>
        </div>

        {/* Feature flags — separate from state toggles below because
         *  these are PRODUCT capabilities the plan unlocks, not
         *  visibility settings. Future shipped flags get added here
         *  one at a time as they wire into actual code. */}
        <div className="pt-2 border-t border-[color:var(--color-rule)]">
          <FieldLabel label="Plan Features" />
          <label
            className="flex items-center gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-2.5 cursor-pointer select-none hover:border-[color:var(--color-accent-border)]"
            title="Unlocks the ProcessPro connector so companies on this plan can pull their SOPs into TaskMagix. Best for customers who subscribe to both TaskMagix and ProcessPro."
          >
            <input
              type="checkbox"
              checked={form.sopIntegration}
              onChange={(e) => setField('sopIntegration', e.target.checked)}
              className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)] shrink-0"
            />
            <span>
              <span className="block text-sm font-medium text-[color:var(--color-ink-strong)]">
                SOP integration
              </span>
              <span className="block text-xs text-[color:var(--color-ink-muted)]">
                Unlocks the ProcessPro connector. Pick this for
                customers who subscribe to both TaskMagix and ProcessPro.
              </span>
            </span>
          </label>
        </div>

        {/* State & ordering — kept compact at the bottom. Toggles
         *  describe how the plan SHOWS UP, not what it does. */}
        <div className="pt-2 border-t border-[color:var(--color-rule)]">
          <FieldLabel label="Visibility & Ordering" />
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm">Active</span>
            </label>
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none"
              title="If off, the plan is hidden from the public Sign-up picker. Master Admin can still assign it to a company from the Companies page."
            >
              <input
                type="checkbox"
                checked={form.isPublic}
                onChange={(e) => setField('isPublic', e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm">Show on Sign-up</span>
            </label>
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none"
              title="Highlights this plan with a “Most Popular” badge on the plan card and the public Sign-up picker. Useful for featuring a tier or a festive offer."
            >
              <input
                type="checkbox"
                checked={form.isPopular}
                onChange={(e) => setField('isPopular', e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm">Mark as Popular</span>
            </label>
            {/* Set-as-default — the plan new companies are auto-assigned
             *  at sign-up (Plan.isDefault). The backend enforces one-at-a-time
             *  (partial-unique index + demote-others in create/update), so the
             *  admin just ticks it; whichever plan was default is moved off
             *  automatically. This is the control the "Default" filter needs. */}
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none"
              title="New companies are automatically assigned this plan during sign-up. Only one plan can be the default — turning this on moves the default off whichever plan currently holds it."
            >
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setField('isDefault', e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
              />
              <span className="text-sm">Set as default plan</span>
            </label>
            <div className="basis-full">
              <label
                className="inline-flex items-center gap-2 cursor-pointer select-none"
                title="Shows a “Talk to sales” button on the website instead of self-serve checkout."
              >
                <input
                  type="checkbox"
                  checked={form.isContactSales}
                  onChange={(e) => setField('isContactSales', e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
                />
                <span className="text-sm">
                  Contact sales (Enterprise / custom pricing)
                </span>
              </label>
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                Shows a “Talk to sales” button on the website instead of
                self-serve checkout.
              </p>
            </div>
          </div>
          <details className="mt-3">
            <summary className="cursor-pointer select-none text-sm font-medium text-[color:var(--color-ink-strong)] marker:text-[color:var(--color-ink-muted)]">
              Advanced settings
            </summary>
            <label className="mt-3 inline-flex items-center gap-2">
              <span className="text-sm text-[color:var(--color-ink-muted)]">
                Sort Order
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                onKeyDown={blockNegativeKey}
                placeholder="0"
                value={String(form.sortOrder)}
                onChange={(e) =>
                  setField(
                    'sortOrder',
                    Math.max(0, Number(stripNegative(e.target.value)) || 0),
                  )
                }
                className="w-20 h-9 px-2 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] text-sm tabular-nums"
              />
            </label>
          </details>
        </div>
        <PreflightChecklist form={form} />
        </div>
      </form>
      {/* Shown on every width. The wrapper above is already
       *  `flex-col lg:flex-row`, so below `lg` this simply stacks under the
       *  form as a full-width card — the layout was built to do that, but the
       *  aside carried `hidden lg:block` and never appeared, leaving a mobile
       *  admin to create a plan without seeing what the customer will
       *  (QA, 2026-09-01). `PlanPreviewCard` has no fixed widths, so it fills
       *  the column cleanly; the 290px track and sticky behaviour stay
       *  desktop-only. */}
      <aside className="lg:w-[290px] shrink-0 lg:sticky lg:top-0 lg:self-start">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-subtle)]">
          Live preview
        </p>
        <PlanPreviewCard form={form} />
      </aside>
      </div>
    </Modal>
  )
}
